import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../services/api";
import { CheckCircle, XCircle } from "lucide-react";

const GRNCancelRequestPage = () => {
  const { indentId, notificationId, notifRemarks } = useParams();
  const remarks_decoded = decodeURIComponent(notifRemarks || "");
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [actionType, setActionType] = useState(null); // 'approve' | 'reject'
  const [success, setSuccess] = useState(false);
  const [successAction, setSuccessAction] = useState(null);

  useEffect(() => {
    setSuccess(false);
    setSuccessAction(null);
    setItems([]);
    if (indentId) fetchIndentDetails();
  }, [indentId, notificationId]);

  const fetchIndentDetails = async () => {
    try {
      setLoading(true);
      const token = window.localStorage.getItem("authToken");
      const response = await fetch(`${API_BASE_URL}/indent/${indentId}/detail`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      const data = await response.json();
      if (!data.error && data.data) setItems(data.data);
    } catch (err) {
      console.error("Error fetching indent details:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (action) => {
    try {
      setActionType(action);
      setSubmitting(true);
      const token = window.localStorage.getItem("authToken");
      const response = await fetch(`${API_BASE_URL}/indent/${indentId}/grn-reversal`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ action }),
      });
      if (response.ok) {
        await fetch(`${API_BASE_URL}/notifications/${notificationId}/read`, {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        setSuccessAction(action);
        setSuccess(true);
        setTimeout(() => navigate(-1), 2000);
      }
    } catch (err) {
      console.error("Error processing GRN reversal:", err);
    } finally {
      setSubmitting(false);
      setActionType(null);
    }
  };

  if (success) {
    const isReject = successAction === 'reject';
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          {isReject ? (
            <XCircle size={64} className="text-red-500 mx-auto mb-4" />
          ) : (
            <CheckCircle size={64} className="text-green-500 mx-auto mb-4" />
          )}
          <h2 className="text-2xl font-bold text-gray-800">
            {isReject ? 'GRN Cancel Request Rejected' : 'GRN Cancelled Successfully'}
          </h2>
          <p className="text-gray-500 mt-2">Redirecting back...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Title */}
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-blue-800">
          GRN Cancel Request — Indent #{indentId}
        </h2>
        <button
          onClick={() => navigate(-1)}
          className="text-sm text-gray-500 hover:text-gray-700 border border-gray-300 px-3 py-1.5 rounded-lg"
        >
          ← Back
        </button>
      </div>

      {/* Reason */}
      {remarks_decoded && (
        <div className="mb-4 px-4 py-2 bg-orange-100 text-orange-800 border border-orange-300 rounded text-sm font-medium">
          🔔 Reason: {remarks_decoded}
        </div>
      )}

      {/* Items Table */}
      {loading ? (
        <div className="flex items-center gap-2 py-8 text-gray-500 text-sm">
          <svg className="animate-spin h-5 w-5 text-blue-600" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a12 12 0 00-12 12h4z" />
          </svg>
          Loading items...
        </div>
      ) : (
        <table className="w-full table-auto border border-gray-300 text-sm mb-6">
          <thead>
            <tr className="bg-gray-100 text-left">
              <th className="border px-4 py-2">#</th>
              <th className="border px-4 py-2">Item Name</th>
              <th className="border px-4 py-2">Unit</th>
              <th className="border px-4 py-2">Ordered Qty</th>
              <th className="border px-4 py-2">Dispatched Qty</th>
              <th className="border px-4 py-2">GRN Qty</th>
              <th className="border px-4 py-2">Rate</th>
              <th className="border px-4 py-2">Amount</th>
              <th className="border px-4 py-2">GST</th>
              <th className="border px-4 py-2">GRN Remarks</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan="10" className="border px-4 py-6 text-center text-gray-400">
                  No items found.
                </td>
              </tr>
            ) : (
              items.map((item, idx) => (
                <tr key={idx} className={item.grn_return === "yes" ? "bg-red-50" : ""}>
                  <td className="border px-4 py-2 text-gray-500">{idx + 1}</td>
                  <td className="border px-4 py-2 font-medium text-gray-800">
                    {item.name}
                    {item.grn_return === "yes" && (
                      <span className="ml-2 text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded">
                        Return
                      </span>
                    )}
                  </td>
                  <td className="border px-4 py-2">{item.unit || "-"}</td>
                  <td className="border px-4 py-2">{parseFloat(item.qty).toFixed(2)}</td>
                  <td className="border px-4 py-2">{item.disp_qty ? parseFloat(item.disp_qty).toFixed(2) : "-"}</td>
                  <td className="border px-4 py-2">{item.grn_qty ?? "-"}</td>
                  <td className="border px-4 py-2">{item.rate ? `₹ ${item.rate}` : "-"}</td>
                  <td className="border px-4 py-2">{item.amount ? `₹ ${item.amount}` : "-"}</td>
                  <td className="border px-4 py-2">{item.gst > 0 ? `${item.gst}%` : "-"}</td>
                  <td className="border px-4 py-2 text-red-600 text-xs">{item.grn_remarks || "-"}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      )}
{/* Actions */}
      {!loading && (
        <div className="flex gap-3 mt-2">
          <button
            onClick={() => navigate(-1)}
            disabled={submitting}
            className="flex items-center gap-2 px-5 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm disabled:opacity-50"
          >
            ← Back
          </button>
          <button
            onClick={() => handleAction('reject')}
            disabled={submitting}
            className="flex items-center gap-2 px-5 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
          >
            <XCircle size={16} />
            {submitting && actionType === 'reject' ? "Rejecting..." : "Reject"}
          </button>
          <button
            onClick={() => handleAction('approve')}
            disabled={submitting}
            className="flex items-center gap-2 px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
          >
            <CheckCircle size={16} />
            {submitting && actionType === 'approve' ? "Processing..." : "Approve GRN Cancel"}
          </button>
        </div>
      )}
    </div>
  );
};

export default GRNCancelRequestPage;